// pages/Register.jsx
import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { auth, db } from '../firebaseConfig';
import {
  createUserWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  linkWithCredential,
  OAuthProvider,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import '../styles/Register.css';
import TitleSetter from '../components/TitleSetter';
import { rateLimiter, formatTimeUntilReset } from '../utils/rateLimiter';
import { validatePassword, getPasswordStrength } from '../utils/passwordValidation';
import { logAuthEvent, EVENT_TYPES } from '../utils/auditLogger';

const Register = () => {
  const [form, setForm] = useState({
    ign: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [pendingCredential, setPendingCredential] = useState(null);
  const [linkMessage, setLinkMessage] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordStrength, setPasswordStrength] = useState('weak');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    // Validate password in real-time
    if (name === 'password') {
      const validation = validatePassword(value);
      setPasswordErrors(validation.errors);
      setPasswordStrength(getPasswordStrength(value));
    } else if (name === 'confirmPassword') {
      // Clear errors if passwords match
      if (value === form.password) {
        setPasswordErrors([]);
      }
    }
  };

  const ensureUserProfile = async (user, profileOverrides = {}) => {
    if (!user) return;
    const userRef = doc(db, `users/${user.uid}`);
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      const [firstName = '', lastName = ''] = (user.displayName || '').split(' ');
      await setDoc(userRef, {
        firstName: profileOverrides.firstName || firstName,
        lastName: profileOverrides.lastName || lastName,
        ign: profileOverrides.ign || user.displayName || '',
        birthdate: profileOverrides.birthdate || '',
        email: user.email,
        role: 'player',
        teamId: null,
        createdAt: serverTimestamp(),
      });
    }
  };

  const postLoginRedirect = async (user) => {
    if (!user) return;
    if (!user.emailVerified) {
      navigate('/verify-email');
      return;
    }

    const userRef = doc(db, `users/${user.uid}`);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      navigate('/onboarding');
    } else if (!userSnap.data().teamId) {
      navigate('/team-decision');
    } else {
      navigate('/my-strategy');
    }
  };

  const register = async (e) => {
    e.preventDefault();

    // Validate password strength
    const passwordValidation = validatePassword(form.password);
    if (!passwordValidation.isValid) {
      alert(`Password requirements not met:\n${passwordValidation.errors.join('\n')}`);
      setPasswordErrors(passwordValidation.errors);
      return;
    }

    if (form.password !== form.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    // Check rate limit
    const check = rateLimiter.checkLimit('REGISTER');
    if (!check.allowed) {
      const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('REGISTER'));
      alert(`Too many registration attempts. Please try again in ${timeUntilReset}.`);
      return;
    }

    setLoading(true);
    try {
      // Record attempt
      if (!rateLimiter.record('REGISTER')) {
        const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('REGISTER'));
        alert(`Too many registration attempts. Please try again in ${timeUntilReset}.`);
        setLoading(false);
        return;
      }

      await setPersistence(auth, browserLocalPersistence);

      const userCred = await createUserWithEmailAndPassword(auth, form.email, form.password);
      const user = userCred.user;

      // ✅ Log registration event
      await logAuthEvent(EVENT_TYPES.REGISTER, 'New user registered', {
        email: form.email,
        ign: form.ign,
      });

      await setDoc(doc(db, `users/${user.uid}`), {
        firstName: '',
        lastName: '',
        ign: form.ign,
        birthdate: '',
        email: form.email,
        role: 'player',
        teamId: null,
        createdAt: serverTimestamp(),
      });

      try {
        await sendEmailVerification(user);
      } catch (verifyErr) {
        console.warn('sendEmailVerification failed', verifyErr);
      }

      // Reset rate limit on successful registration
      rateLimiter.reset('REGISTER');
      
      navigate('/verify-email');
    } catch (err) {
      console.error('Registration failed:', err.message);
      
      // Don't reset rate limit on failure
      const check = rateLimiter.checkLimit('REGISTER');
      const remaining = check.remaining;
      const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('REGISTER'));
      
      if (remaining === 0) {
        alert(`Too many failed registration attempts. Please try again in ${timeUntilReset}.`);
      } else {
        alert(`Registration error: ${err.message}. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setLoading(true);
    setLinkMessage('');
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      await ensureUserProfile(user);
      await postLoginRedirect(user);
    } catch (err) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        const email = err.customData?.email || '';
        const cred = GoogleAuthProvider.credentialFromError(err);
        setLinkEmail(email);
        setPendingCredential(cred);
        setLinkMessage('We found an existing email/password account. Sign in once to link Google.');
      } else {
        alert(err.message || 'Google sign-in failed.');
      }
      console.error('Google sign-up error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignUp = async () => {
    setLoading(true);
    setLinkMessage('');
    try {
      const provider = new OAuthProvider('apple.com');
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      await ensureUserProfile(user);
      await postLoginRedirect(user);
    } catch (err) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        const email = err.customData?.email || '';
        const cred = OAuthProvider.credentialFromError(err);
        setLinkEmail(email);
        setPendingCredential(cred);
        setLinkMessage('We found an existing email/password account. Sign in once to link Apple.');
      } else {
        alert(err.message || 'Apple sign-in failed.');
      }
      console.error('Apple sign-up error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkAccount = async (e) => {
    e.preventDefault();
    if (!pendingCredential || !linkEmail || !linkPassword) return;
    setLoading(true);
    try {
      const userCred = await signInWithEmailAndPassword(auth, linkEmail, linkPassword);
      await linkWithCredential(userCred.user, pendingCredential);
      await ensureUserProfile(userCred.user);
      setPendingCredential(null);
      setLinkPassword('');
      setLinkMessage('');
      await postLoginRedirect(userCred.user);
    } catch (err) {
      console.error('Link account error', err);
      alert(err.message || 'Failed to link Google. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TitleSetter title="Register" />
      <div className="login-container register-container">
        <header className="login-topbar">
          <button className="login-brand" onClick={() => navigate('/landing')} aria-label="Back to TheBox">
            <img src="/assets/theboxlogo.png" alt="TheBox" />
          </button>
          <button className="back-site-btn" onClick={() => navigate('/landing')}>
            Back to site <span aria-hidden="true">&rarr;</span>
          </button>
        </header>

        <div className="login-wrapper register-wrapper">
          <div className="logo-side">
            <div className="login-brand-copy">
              <p className="login-eyebrow">Private team workspace</p>
              <h1>Built for contenders.</h1>
              <p>
                Your competitive workspace for cleaner prep, faster reviews, and better calls.
              </p>
              <h2>Plan. Brief. Scrim. Review. Adapt.</h2>
              <div className="login-proof-points">
                <span>Visual Playbooks</span>
                <span>Stats & Scouting</span>
                <span>Team Calendar</span>
              </div>
            </div>

            <div className="login-trust-note">
              <strong>Private team workspace.</strong>
              <span>Your playbooks, scouting, and stats stay within your team's workspace.</span>
            </div>
          </div>

          <div className="login-card register-card">
            <div className="login-card-header">
              <p className="login-eyebrow">Create account</p>
              <h1>Join TheBox</h1>
              <p>Registration is available to approved beta teams.</p>
            </div>

            <button
              type="button"
              className="social-btn"
              onClick={handleGoogleSignUp}
              disabled={loading}
            >
              <img src="/global%20logos/Google__G__logo.svg.png" alt="" className="social-icon" />
              Continue with Google
            </button>
            <button
              type="button"
              className="social-btn"
              onClick={handleAppleSignUp}
              disabled={loading}
            >
              <img src="/global%20logos/Apple_logo_black.svg" alt="" className="social-icon" />
              Continue with Apple
            </button>

            <div className="divider-row">
              <span />
              <p>OR</p>
              <span />
            </div>

            <form onSubmit={register} className="register-form">
              <label className="field-label" htmlFor="register-email">Email</label>
              <input
                id="register-email"
                name="email"
                type="email"
                placeholder="you@example.com"
                required
                value={form.email}
                onChange={handleChange}
              />

              <label className="field-label" htmlFor="register-password">Password</label>
              <div className="password-field">
                <div className="password-input-wrap">
                  <input
                    id="register-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    required
                    value={form.password}
                    onChange={handleChange}
                    className={
                      form.password
                        ? `password-strength-input password-strength-${passwordStrength}`
                        : ''
                    }
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="password-help">
                  Use 12+ characters with uppercase, lowercase, a number, and a symbol.
                </p>
                {form.password && (
                  <div className="password-feedback" aria-live="polite">
                    <div className={`password-strength-label password-strength-${passwordStrength}`}>
                      Strength: {passwordStrength.toUpperCase()}
                    </div>
                    {passwordErrors.length > 0 && (
                      <ul>
                        {passwordErrors.map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <label className="field-label" htmlFor="register-confirm-password">Confirm password</label>
              <div className="password-input-wrap">
                <input
                  id="register-confirm-password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm password"
                  required
                  value={form.confirmPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword((value) => !value)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showConfirmPassword}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {form.confirmPassword && form.password !== form.confirmPassword && (
                <p className="field-error" aria-live="polite">Passwords do not match</p>
              )}

              <label className="field-label" htmlFor="register-ign">In-game name</label>
              <input
                id="register-ign"
                name="ign"
                placeholder="Displayed publicly"
                value={form.ign}
                onChange={handleChange}
              />

              <button type="submit" className="login-submit-btn" disabled={loading}>
                {loading ? 'Creating account...' : <>Create Account <span aria-hidden="true">&rarr;</span></>}
              </button>
            </form>

            {pendingCredential && (
              <form className="link-box" onSubmit={handleLinkAccount}>
                <p className="link-text">
                  {linkMessage || 'Existing account found. Enter your password to link Google.'}
                </p>
                <input
                  type="password"
                  placeholder="Password"
                  value={linkPassword}
                  onChange={(e) => setLinkPassword(e.target.value)}
                  required
                />
                <button type="submit" disabled={loading}>
                  {loading ? 'Linking...' : 'Link and continue'}
                </button>
              </form>
            )}

            <p className="register-text">
              Already have an account?{' '}
              <button type="button" className="register-link" onClick={() => navigate('/login')}>
                Sign in <span aria-hidden="true">&rarr;</span>
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
